import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format, isPast, isSameDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { validateCliente } from '../utils/validators';
import html2canvas from 'html2canvas';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    total_clientes: 0,
    total_servicios: 0,
    citas_hoy: 0,
    citas_pendientes: 0,
    citas_mes: 0,
    citas_mes_canceladas: 0,
    tasa_cancelacion: 0,
    ingresos_mes: 0,
    clientes_nuevos_mes: 0,
    clientes_nuevos_anterior: 0,
    top_servicios: [],
    top_profesionales: [],
    citas_por_dia: [],
    mes_label: '',
  });
  const [loading, setLoading] = useState(true);
  const [mesActual, setMesActual] = useState(new Date());
  const [citas, setCitas] = useState([]);
  const [excepciones, setExcepciones] = useState([]);
  const [diasBase, setDiasBase] = useState([]);
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [citasDia, setCitasDia] = useState([]);
  const [excepcionesDia, setExcepcionesDia] = useState([]);
  const [totalHorasDisponibles, setTotalHorasDisponibles] = useState(0);
  const [horarioDia, setHorarioDia] = useState(null);
  
  // Lista de espera
  const [listaEspera, setListaEspera] = useState([]);

  // Estados para el popup de crear cita rápida
  const [showQuickCitaModal, setShowQuickCitaModal] = useState(false);
  const [showFueraHorarioModal, setShowFueraHorarioModal] = useState(false);
  const [clientes, setClientes] = useState([]);
  // Cita rápida: se puede agendar a un cliente ('c:<id>') o a un prospecto del CRM ('p:<id>')
  const [prospectosQuick, setProspectosQuick] = useState([]);
  const [contactoQuick, setContactoQuick] = useState('');
  const [busquedaContacto, setBusquedaContacto] = useState('');
  const [servicios, setServicios] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [quickCitaForm, setQuickCitaForm] = useState({
    cliente_id: '',
    servicio_id: '',
    fecha_cita: '',
    hora_inicio: '09:00',
    notas: ''
  });
  const [horasDisponiblesCita, setHorasDisponiblesCita] = useState([]);
  const [esRecurrenteDash, setEsRecurrenteDash] = useState(false);
  const [recurrenciaDash, setRecurrenciaDash] = useState({
    tipo: 'semanal', fin_tipo: 'sesiones', num_sesiones: 8, fecha_fin: ''
  });
  
  // Estados para modales de listas
  const [showCitasPendientesModal, setShowCitasPendientesModal] = useState(false);
  const [showServiciosActivosModal, setShowServiciosActivosModal] = useState(false);
  const [citasPendientesLista, setCitasPendientesLista] = useState([]);
  const [serviciosActivosLista, setServiciosActivosLista] = useState([]);
  
  // Estado para el modal de nuevo cliente
  const [showNuevoClienteModal, setShowNuevoClienteModal] = useState(false);
  const [nuevoClienteForm, setNuevoClienteForm] = useState({
    nombres: '', apellidos: '', identificacion: '', whatsapp_number: '', email: ''
  });

  // Estado para el modal de nuevo servicio
  const [showNuevoServicioModal, setShowNuevoServicioModal] = useState(false);
  const [nuevoServicioForm, setNuevoServicioForm] = useState({
    nombre: '', descripcion: '', duracion_minutos: 30, precio: ''
  });

  // Estado para el modal de nuevo profesional
  const [showNuevoProfesionalModal, setShowNuevoProfesionalModal] = useState(false);
  const [nuevoProfesionalForm, setNuevoProfesionalForm] = useState({
    nombres: '', apellidos: '', email: '', telefono: '', especialidad: ''
  });

  // Estados para el modal de notas
  const [showNotasModal, setShowNotasModal] = useState(false);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [notasDiagnostico, setNotasDiagnostico] = useState('');
  const [mostrarAlertaPago, setMostrarAlertaPago] = useState(false);
  const [diasVencimiento, setDiasVencimiento] = useState(0);
  const [diasRestantes, setDiasRestantes] = useState(0);
  const [suscripcionActiva, setSuscripcionActiva] = useState(null);

  const [generandoImagen, setGenerandoImagen] = useState(false);
  const imagenRef = useRef(null);

  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const diasNombres = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  useEffect(() => {
    if (authLoading) return;
    if (user?.tenant?.acceso_bloqueado === true) {
      setLoading(false);
      return;
    }
    fetchStats();
    fetchCalendarioData();
    fetchClientes();
    fetchServicios();
  }, [authLoading]);

  useEffect(() => {
    if (authLoading || user?.tenant?.acceso_bloqueado === true) return;
    fetchCalendarioData();
  }, [mesActual]);

  // Actualizar estadísticas periódicamente
  useEffect(() => {
    if (authLoading || user?.tenant?.acceso_bloqueado === true) return;
    const interval = setInterval(() => {
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [authLoading]);

  // Actualizar calendario periódicamente para reflejar reservas online nuevas
  useEffect(() => {
    if (authLoading || user?.tenant?.acceso_bloqueado === true) return;
    const interval = setInterval(() => {
      fetchCalendarioData();
    }, 60000);

    return () => clearInterval(interval);
  }, [authLoading, mesActual]);

  // Cerrar dropdown de servicios al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-service-combobox')) {
        setShowServiceDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);


  useEffect(() => {
  // Solo verificar pago si NO es superadmin
  if (user?.rol !== 'superadmin') {
    verificarPagoPendiente();
  }
}, [user]);

const verificarPagoPendiente = async () => {
  try {
    const { data } = await api.get('/planes/estado');
    setMostrarAlertaPago(!data.pagado && data.dias_vencimiento <= 3);
    setDiasVencimiento(data.dias_vencimiento);
    setDiasRestantes(data.dias_vencimiento);
    setSuscripcionActiva(data.tiene_suscripcion ? data : null);
  } catch {
    setMostrarAlertaPago(false);
    setDiasRestantes(0);
    setSuscripcionActiva(null);
  }
};

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      setStats(data);
    } catch {
    }
  };

  const fetchClientes = async () => {
    try {
      const { data } = await api.get('/clientes');
      setClientes(data);
    } catch {
    }
  };

  const fetchServicios = async () => {
    try {
      const { data } = await api.get('/servicios');
      setServicios(data);
    } catch {
    }
  };

  const fetchCalendarioData = async () => {
    try {
      const year = mesActual.getFullYear();
      const month = mesActual.getMonth() + 1;
      const fechaInicio = format(new Date(year, month - 1, 1), 'yyyy-MM-dd');
      const fechaFin    = format(new Date(year, month, 0),    'yyyy-MM-dd');

      // Citas, espera y sucursales en paralelo
      const [citasRes, esperaRes, sucursalesRes] = await Promise.all([
        api.get('/citas/todas', { params: { fecha_inicio: fechaInicio, fecha_fin: fechaFin } }),
        api.get('/lista-espera').catch(() => ({ data: [] })),
        api.get('/sucursales').catch(() => ({ data: [] })),
      ]);

      setCitas(citasRes.data || []);
      setListaEspera(esperaRes.data || []);

      const sucursales = sucursalesRes.data || [];
      if (sucursales.length > 0) {
        const sucursalPrincipal = sucursales.find(s => s.es_principal) || sucursales[0];
        const { data: horarios } = await api.get(`/horarios/sucursal/${sucursalPrincipal.id}`);
        const horarioActivo = horarios.find(h => h.activo);

        if (horarioActivo) {
          // Horario completo y excepciones en paralelo
          const [horarioCompleto, excepcionesData] = await Promise.all([
            api.get(`/horarios/${horarioActivo.id}/completo`),
            api.get(`/horarios/${horarioActivo.id}/excepciones/mes/${year}/${month}`),
          ]);
          setDiasBase(horarioCompleto.data.dias_base || []);
          setExcepciones(excepcionesData.data || []);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleEsperaEstado = async (id, estado) => {
    try {
      await api.patch(`/lista-espera/${id}/estado`, { estado });
      setListaEspera(le => le.map(x => x.id === id ? { ...x, estado } : x));
      toast.success('Actualizado');
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleEsperaEliminar = async (id) => {
    try {
      await api.delete(`/lista-espera/${id}`);
      setListaEspera(le => le.filter(x => x.id !== id));
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleAbrirModalNuevoCliente = () => {
    setNuevoClienteForm({ nombres: '', apellidos: '', identificacion: '', whatsapp_number: '', email: '' });
    setShowNuevoClienteModal(true);
  };

  const handleCerrarModalNuevoCliente = () => {
    setShowNuevoClienteModal(false);
  };

  const handleCrearCliente = async (e) => {
    e.preventDefault();
    const validationError = validateCliente(nuevoClienteForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      const { data } = await api.post('/clientes', nuevoClienteForm);
      if (data.error) {
        toast.error(data.error);
        return;
      }
      toast.success(data.message || 'Cliente creado');
      handleCerrarModalNuevoCliente();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear cliente');
    }
  };

  const handleAbrirModalNuevoServicio = () => {
    setNuevoServicioForm({ nombre: '', descripcion: '', duracion_minutos: 30, precio: '' });
    setShowNuevoServicioModal(true);
  };

  const handleCerrarModalNuevoServicio = () => {
    setShowNuevoServicioModal(false);
  };

  const handleCrearServicio = async (e) => {
    e.preventDefault();
    if (nuevoServicioForm.duracion_minutos <= 0) {
      toast.error('La duración debe ser mayor a 0 minutos');
      return;
    }
    if (nuevoServicioForm.duracion_minutos > 120) {
      toast.error('La duración no puede exceder los 120 minutos');
      return;
    }
    try {
      await api.post('/servicios', {
        ...nuevoServicioForm,
        duracion_minutos: Number(nuevoServicioForm.duracion_minutos),
        requiere_deposito: 0,
        monto_deposito: null
      });
      toast.success('Servicio creado');
      handleCerrarModalNuevoServicio();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear servicio');
    }
  };

  const handleAbrirModalNuevoProfesional = () => {
    setNuevoProfesionalForm({ nombres: '', apellidos: '', email: '', telefono: '', especialidad: '' });
    setShowNuevoProfesionalModal(true);
  };

  const handleCerrarModalNuevoProfesional = () => {
    setShowNuevoProfesionalModal(false);
  };

  const handleCrearProfesional = async (e) => {
    e.preventDefault();
    if (!nuevoProfesionalForm.nombres || !nuevoProfesionalForm.apellidos) {
      toast.error('Nombres y apellidos son obligatorios');
      return;
    }
    try {
      await api.post('/profesionales', nuevoProfesionalForm);
      toast.success('Profesional creado');
      handleCerrarModalNuevoProfesional();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear profesional');
    }
  };

  const generarImagenHorario = async (accion) => {
    if (!imagenRef.current) return;
    setGenerandoImagen(true);
    try {
      const canvas = await html2canvas(imagenRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      canvas.toBlob(async (blob) => {
        if (accion === 'descargar') {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `horario-${fechaSeleccionada}.png`;
          a.click();
          URL.revokeObjectURL(url);
        } else if (accion === 'whatsapp') {
          if (navigator.share) {
            const file = new File([blob], `horario-${fechaSeleccionada}.png`, { type: 'image/png' });
            await navigator.share({ files: [file], title: 'Horario disponible' });
          } else {
            toast.info('Compartir no está disponible en este navegador. Usa Descargar.');
          }
        }
      }, 'image/png');
    } catch {
      toast.error('Error al generar la imagen');
    } finally {
      setGenerandoImagen(false);
    }
  };

  const obtenerHorarioDia = (fecha) => {
    const fechaObj = new Date(fecha + 'T00:00:00');
    const diaSemana = fechaObj.getDay();
    
    const excepcionesDelDia = excepciones.filter(e => normalizarFecha(e.fecha) === fecha);
    const tieneCerrado = excepcionesDelDia.some(e => e.tipo === 'cerrado');
    if (tieneCerrado) return { abierto: false, motivo: 'cerrado' };
    
    const excepcionEspecial = excepcionesDelDia.find(e => e.tipo === 'horario_especial');
    
    if (excepcionEspecial) {
      return {
        abierto: true,
        apertura: excepcionEspecial.hora_inicio?.substring(0,5),
        cierre: excepcionEspecial.hora_fin?.substring(0,5),
        especial: true
      };
    }
    
    const horarioBase = diasBase.find(d => d.dia_semana === diaSemana);
    if (horarioBase && horarioBase.esta_abierto) {
      return {
        abierto: true,
        apertura: horarioBase.hora_apertura?.substring(0,5),
        cierre: horarioBase.hora_cierre?.substring(0,5)
      };
    }
    
    return { abierto: false, motivo: 'horario' };
  };

  const calcularTotalHorasDisponibles = (fecha, excepcionesDelDia, citasDelDia) => {
    const horario = obtenerHorarioDia(fecha);
    if (!horario.abierto) return 0;

    const [hInicio, mInicio] = horario.apertura.split(':').map(Number);
    const [hFin, mFin] = horario.cierre.split(':').map(Number);

    const minutosTotal = (hFin * 60 + mFin) - (hInicio * 60 + mInicio);
    if (minutosTotal <= 0) return 0;

    let minutosOcupados = 0;
    citasDelDia.forEach(cita => {
      if (!cita.hora_inicio || cita.estado === 'cancelada') return;
      const servicioCita = servicios.find(s => s.id == cita.servicio_id);
      minutosOcupados += servicioCita?.duracion_minutos || 60;
    });

    const minutosDisponibles = Math.max(0, minutosTotal - minutosOcupados);
    return parseFloat((minutosDisponibles / 60).toFixed(1));
  };

  // Sin límite por horario: se ofrecen todas las horas de atención (varios clientes
  // pueden agendarse a la misma hora); solo se ocultan las horas ya pasadas de hoy.
  const cargarHorasDisponiblesParaCita = async (fecha) => {
    try {
      const fechaStr = format(new Date(fecha + 'T00:00:00'), 'yyyy-MM-dd');
      const { data: todasHoras } = await api.get(`/citas/horas-disponibles?fecha=${fechaStr}`);

      const hoy = new Date();
      if (fecha === format(hoy, 'yyyy-MM-dd')) {
        const horaActual = hoy.toTimeString().substring(0, 5);
        setHorasDisponiblesCita(todasHoras.filter(hora => hora > horaActual));
      } else {
        setHorasDisponiblesCita(todasHoras);
      }
    } catch {
      setHorasDisponiblesCita([]);
    }
  };

  const cambiarMes = (direccion) => {
    const nuevaFecha = new Date(mesActual);
    nuevaFecha.setMonth(nuevaFecha.getMonth() + direccion);
    setMesActual(nuevaFecha);
    setDiaSeleccionado(null);
    setFechaSeleccionada(null);
  };

  const irHoy = () => {
    setMesActual(new Date());
    setDiaSeleccionado(null);
    setFechaSeleccionada(null);
  };

  const handleClickDia = (dia, fecha, citasDelDia, excepcionesDelDia) => {
    setDiaSeleccionado(dia);
    setFechaSeleccionada(fecha);
    setCitasDia(citasDelDia);
    setExcepcionesDia(excepcionesDelDia || []);
    setHorarioDia(obtenerHorarioDia(fecha));
    
    const total = calcularTotalHorasDisponibles(fecha, excepcionesDelDia || [], citasDelDia);
    setTotalHorasDisponibles(total);
  };

  const handleQuickCitaClick = () => {
    const esHoy = fechaSeleccionada === format(new Date(), 'yyyy-MM-dd');
    const horaActual = new Date().toTimeString().substring(0, 5);
    if (esHoy && horarioDia?.cierre && horaActual >= horarioDia.cierre) {
      setShowFueraHorarioModal(true);
      return;
    }
    setQuickCitaForm({
      ...quickCitaForm,
      fecha_cita: fechaSeleccionada,
      hora_inicio: '09:00'
    });
    cargarHorasDisponiblesParaCita(fechaSeleccionada);
    setContactoQuick('');
    setBusquedaContacto('');
    fetchClientes();
    // Prospectos del CRM (si el tenant no tiene CRM, la lista queda vacía)
    api.get('/crm/prospectos', { params: { es_cliente: 'false' } })
      // Los prospectos ya vinculados a un cliente salen en el grupo "Clientes"
      .then(({ data }) => setProspectosQuick((data?.prospectos || []).filter(p => !p.cliente_id)))
      .catch(() => setProspectosQuick([]));
    setShowQuickCitaModal(true);
  };

  const handleQuickCitaSubmit = async (e) => {
    e.preventDefault();
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const [year, month, day] = quickCitaForm.fecha_cita.split('-').map(Number);
    const fechaSeleccionadaObj = new Date(year, month - 1, day);
    fechaSeleccionadaObj.setHours(0, 0, 0, 0);
    
    if (fechaSeleccionadaObj < hoy) {
      toast.error('❌ No puedes agendar citas en fechas pasadas');
      return;
    }
    
    const fechaHoyStr = format(new Date(), 'yyyy-MM-dd');
    if (quickCitaForm.fecha_cita === fechaHoyStr) {
      const horaActual = new Date().toTimeString().substring(0, 5);
      if (quickCitaForm.hora_inicio <= horaActual) {
        toast.error('❌ No puedes agendar citas en horas pasadas');
        return;
      }
    }
    
    if (!contactoQuick) {
      toast.error('Selecciona un cliente o prospecto');
      return;
    }
    const [tipoContacto, idContacto] = contactoQuick.split(':');
    const { cliente_id: _omit, ...restoForm } = quickCitaForm;
    const payloadCita = tipoContacto === 'p'
      ? { ...restoForm, prospecto_id: idContacto }
      : { ...restoForm, cliente_id: idContacto };

    try {
      if (esRecurrenteDash) {
        if (recurrenciaDash.fin_tipo === 'fecha' && !recurrenciaDash.fecha_fin) {
          toast.error('Debes indicar la fecha límite de la serie');
          return;
        }
        const { data } = await api.post('/citas/recurrentes', {
          ...payloadCita,
          fecha_inicio: quickCitaForm.fecha_cita,
          tipo_recurrencia: recurrenciaDash.tipo,
          fin_tipo: recurrenciaDash.fin_tipo,
          num_sesiones: recurrenciaDash.num_sesiones,
          fecha_fin: recurrenciaDash.fecha_fin,
        });
        const omit = data.omitidas > 0 ? ` (${data.omitidas} omitida${data.omitidas !== 1 ? 's' : ''} por horario)` : '';
        toast.success(`✅ ${data.creadas} cita${data.creadas !== 1 ? 's' : ''} recurrente${data.creadas !== 1 ? 's' : ''} creada${data.creadas !== 1 ? 's' : ''}${omit}`);
      } else {
        await api.post('/citas', payloadCita);
        toast.success('✅ Cita creada rápidamente');
      }
      setShowQuickCitaModal(false);
      
      await fetchCalendarioData();
      
      const { data } = await api.get('/citas/todas');
      const hoyFecha = format(new Date(), 'yyyy-MM-dd');
      const citasHoyCount = data.filter(c => normalizarFecha(c.fecha_cita) === hoyFecha && c.estado !== 'cancelada').length;
      const citasPendientesCount = data.filter(c => c.estado === 'pendiente').length;
      
      setStats(prev => ({
        ...prev,
        citas_hoy: citasHoyCount,
        citas_pendientes: citasPendientesCount
      }));
      
      if (diaSeleccionado && fechaSeleccionada) {
        const fechaStr = format(new Date(fechaSeleccionada + 'T00:00:00'), 'yyyy-MM-dd');
        const citasActualizadas = data.filter(c => normalizarFecha(c.fecha_cita) === fechaStr);
        setCitasDia(citasActualizadas);
        
        const excepcionesDelDia = excepciones.filter(e => 
          normalizarFecha(e.fecha) === fechaSeleccionada
        );
        const total = calcularTotalHorasDisponibles(
          fechaSeleccionada, 
          excepcionesDelDia, 
          citasActualizadas
        );
        setTotalHorasDisponibles(total);
      }
      
      setQuickCitaForm({
        cliente_id: '',
        servicio_id: '',
        fecha_cita: '',
        hora_inicio: '09:00',
        notas: ''
      });
      setContactoQuick('');
      setBusquedaContacto('');
      setEsRecurrenteDash(false);
      setRecurrenciaDash({ tipo: 'semanal', fin_tipo: 'sesiones', num_sesiones: 8, fecha_fin: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear cita');
    }
  };

  const handleGuardarNotas = async () => {
    try {
      await api.patch(`/citas/${citaSeleccionada.id}/notas`, { 
        notas: notasDiagnostico 
      });
      toast.success('📝 Notas guardadas');
      setShowNotasModal(false);
      
      await fetchCalendarioData();
      await fetchStats();
      
      if (fechaSeleccionada) {
        const fechaStr = format(new Date(fechaSeleccionada + 'T00:00:00'), 'yyyy-MM-dd');
        const { data: citasActualizadas } = await api.get('/citas', {
          params: {
            fecha_inicio: fechaStr,
            fecha_fin: fechaStr
          }
        });
        setCitasDia(citasActualizadas || []);
      }
    } catch (error) {
      toast.error('Error al guardar notas');
    }
  };

  const handleVerCitasPendientes = async () => {
    try {
      const { data } = await api.get('/citas/todas');
      const pendientes = data.filter(c => c.estado === 'pendiente');
      setCitasPendientesLista(pendientes);
      setShowCitasPendientesModal(true);
    } catch {
      toast.error('Error al cargar citas pendientes');
    }
  };

  const handleVerServiciosActivos = () => {
    const activos = servicios.filter(s => s.activo === 1 || s.activo === true);
    setServiciosActivosLista(activos);
    setShowServiciosActivosModal(true);
  };

  const normalizarFecha = (fecha) => {
    if (!fecha) return '';
    if (typeof fecha === 'string') {
      return fecha.split('T')[0].split(' ')[0];
    }
    if (fecha instanceof Date) {
      return format(fecha, 'yyyy-MM-dd');
    }
    return '';
  };

  const puedeAgendarCita = (fecha) => {
    const fechaObj = new Date(fecha + 'T00:00:00');
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    if (fechaObj < hoy) return false;

    const horario = obtenerHorarioDia(fecha);
    // Bloquear si hay excepción de cerrado, o si hay horario configurado y el día no está abierto
    if (!horario.abierto && (horario.motivo === 'cerrado' || diasBase.length > 0)) return false;

    if (isSameDay(fechaObj, new Date())) {
      const horaActual = new Date().toTimeString().substring(0, 5);
      if (horario.cierre && horaActual >= horario.cierre) return false;
    }

    return true;
  };

  const renderCalendario = () => {
    const year = mesActual.getFullYear();
    const month = mesActual.getMonth();
    const primerDia = new Date(year, month, 1).getDay();
    const diasEnMes = new Date(year, month + 1, 0).getDate();
    const hoy = new Date();
    const hoyDate = format(hoy, 'yyyy-MM-dd');
    
    const esHoy = (dia) => {
      const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      return fechaStr === hoyDate;
    };

    const calendario = [];
    let dia = 1;

    for (let semana = 0; semana < 6; semana++) {
      const diasSemana = [];
      
      for (let diaSemana = 0; diaSemana < 7; diaSemana++) {
        if ((semana === 0 && diaSemana < primerDia) || dia > diasEnMes) {
          diasSemana.push(
            <div key={`vacio-${semana}-${diaSemana}`} className="calendario-dia vacio"></div>
          );
        } else {
          const diaActual = dia;
          const fecha = `${year}-${String(month + 1).padStart(2, '0')}-${String(diaActual).padStart(2, '0')}`;
          
          const citasDelDia = citas.filter(c => normalizarFecha(c.fecha_cita) === fecha);
          const excepcionesDelDia = excepciones.filter(e => normalizarFecha(e.fecha) === fecha);
          const horario = obtenerHorarioDia(fecha);
          
          const esPasado = !esHoy(diaActual) && new Date(fecha + 'T00:00:00') < hoy;

          let clasesDia = 'calendario-dia';
          let estaCerrado = false;
          let horasAtencion = null;

          if (esHoy(diaActual)) clasesDia += ' hoy';
          else if (esPasado) clasesDia += ' pasado';
          if (diaSeleccionado === diaActual) clasesDia += ' seleccionado';
          if (citasDelDia.length > 0) clasesDia += ' tiene-citas';
          
          const tieneCerrado = excepcionesDelDia.some(e => e.tipo === 'cerrado');
          if (tieneCerrado) {
            clasesDia += ' cerrado';
            estaCerrado = true;
          } else if (excepcionesDelDia.some(e => e.tipo === 'horario_especial')) {
            clasesDia += ' especial';
            const especial = excepcionesDelDia.find(e => e.tipo === 'horario_especial');
            horasAtencion = `${especial.hora_inicio?.substring(0,5)} - ${especial.hora_fin?.substring(0,5)}`;
          } else if (horario && !horario.abierto) {
            clasesDia += ' cerrado-base';
            estaCerrado = true;
          } else if (horario && horario.abierto) {
            horasAtencion = `${horario.apertura} - ${horario.cierre}`;
          }

          diasSemana.push(
            <div 
              key={`dia-${diaActual}`} 
              className={clasesDia}
              onClick={() => handleClickDia(diaActual, fecha, citasDelDia, excepcionesDelDia)}
            >
              <div className="dia-numero">{diaActual}</div>
              
              {excepcionesDelDia.length > 0 && (
                <div className="dia-excepciones-multiple">
                  {excepcionesDelDia.slice(0, 3).map((exc, idx) => (
                    <div 
                      key={exc.id || idx} 
                      className="dia-excepcion-item"
                      title={exc.motivo || exc.tipo}
                    >
                      {exc.tipo === 'cerrado' ? '🚫' : '⏰'}
                    </div>
                  ))}
                  {excepcionesDelDia.length > 3 && (
                    <div className="dia-excepcion-mas" title={`${excepcionesDelDia.length - 3} excepciones más`}>
                      +{excepcionesDelDia.length - 3}
                    </div>
                  )}
                </div>
              )}
              
              {!estaCerrado && horasAtencion && (
                <div className="dia-horario">{horasAtencion}</div>
              )}
              
              {citasDelDia.length > 0 && (
                <div className="dia-citas-indicador">
                  <span className="citas-count">{citasDelDia.length}</span> cita(s)
                </div>
              )}
            </div>
          );
          dia++;
        }
      }
      
      calendario.push(
        <div key={`semana-${semana}`} className="calendario-semana">
          {diasSemana}
        </div>
      );
      
      if (dia > diasEnMes) break;
    }

    return calendario;
  };

  const statCards = [
    { title: 'Total Clientes', value: stats.total_clientes, icon: '👥', color: 'blue', onClick: null },
    { title: 'Servicios Activos', value: stats.total_servicios, icon: '💼', color: 'green', onClick: handleVerServiciosActivos },
    { title: 'Citas Hoy', value: stats.citas_hoy, icon: '📅', color: 'purple', onClick: null },
    { title: 'Citas Pendientes', value: stats.citas_pendientes, icon: '⏰', color: 'orange', onClick: handleVerCitasPendientes }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen">
          <div className="loading"></div>
          <p>Cargando dashboard...</p>
        </div>
      </Layout>
    );
  }

  const puedeCrearCita = fechaSeleccionada ? puedeAgendarCita(fechaSeleccionada) : false;

  // Hoy fuera de horario: botón habilitado para mostrar el modal de aviso (no disabled)
  const esHoyFueraHorario = (() => {
    if (!fechaSeleccionada) return false;
    const fechaObj = new Date(fechaSeleccionada + 'T00:00:00');
    if (!isSameDay(fechaObj, new Date())) return false;
    const horario = obtenerHorarioDia(fechaSeleccionada);
    const horaActual = new Date().toTimeString().substring(0, 5);
    return !!(horario.cierre && horaActual >= horario.cierre);
  })();

  return (
    <Layout>
      <div className="dashboard">
        <div className="page-header" style={{ marginBottom: '0.25rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>👋 Hola, {user?.nombre || 'Bienvenido'}</h2>
            <p className="page-subtitle" style={{ margin: '0.2rem 0 0' }}>
              {new Date().toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        {mostrarAlertaPago && (
          <div className="alert alert-danger" style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            borderRadius: '12px',
            background: '#fee2e2',
            borderLeft: '4px solid #dc2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <strong>⚠️ Pago Pendiente</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                Tu suscripción vence en {diasVencimiento} día(s). Realiza el pago para continuar disfrutando de todos los beneficios.
              </p>
            </div>
            <a href="/suscripcion/pagar" className="btn btn-danger btn-sm">
              Pagar ahora
            </a>
          </div>
        )}


        {/* Link de reserva pública + Acciones rápidas */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'stretch' }}>
          {user?.tenant?.codigo && (
            <div style={{
              flex: '2 1 320px',
              padding: '1rem 1.25rem',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '1px solid #bfdbfe',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#1d4ed8', marginBottom: '0.35rem' }}>
                  🔗 Tu link de reserva online
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#1e40af',
                  fontFamily: 'monospace',
                  background: '#fff',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe',
                  wordBreak: 'break-all',
                }}>
                  {window.location.origin}/agenda/reservar/{user.tenant.codigo}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/agenda/reservar/${user.tenant.codigo}`);
                    toast.success('¡Link copiado!');
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  📋 Copiar
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Reserva tu cita aquí: ${window.location.origin}/agenda/reservar/${user.tenant.codigo}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#25d366',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  WhatsApp
                </a>
              </div>
            </div>
          )}

          <div className="card" style={{ flex: '1 1 260px', margin: 0 }}>
            <div className="card-title">🎯 Acciones Rápidas</div>
            <div className="quick-actions">
              <button type="button" onClick={handleAbrirModalNuevoCliente} className="quick-action-btn">
                <span className="action-icon" style={{ fontSize: '1.2rem' }}>👤</span>
                <span>Nuevo Cliente</span>
              </button>
              <button type="button" onClick={handleAbrirModalNuevoServicio} className="quick-action-btn">
                <span className="action-icon" style={{ fontSize: '1.2rem' }}>💼</span>
                <span>Nuevo Servicio</span>
              </button>
              <button type="button" onClick={handleAbrirModalNuevoProfesional} className="quick-action-btn">
                <span className="action-icon" style={{ fontSize: '1.2rem' }}>👨‍💼</span>
                <span>Nuevo Profesional</span>
              </button>
              <a href="/agenda/reportes" className="quick-action-btn">
                <span className="action-icon" style={{ fontSize: '1.2rem' }}>📈</span>
                <span>Ver Reportes</span>
              </a>
              {user?.tenant?.plan !== 'Trial' && (
                <a href="/agenda/app_movil/app-agenda.apk" download className="quick-action-btn">
                  <span className="action-icon" style={{ fontSize: '1.2rem' }}>⬇️</span>
                  <span>App Móvil</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Calendario Principal */}
        <div className="card calendario-card">
          <div className="calendario-header">
            <div className="calendario-titulo">
              <h3>📅 {meses[mesActual.getMonth()]} {mesActual.getFullYear()}</h3>
            </div>
            <div className="calendario-controles">
              <button className="btn btn-sm btn-secondary" onClick={() => cambiarMes(-1)}>← Anterior</button>
              <button className="btn btn-sm btn-primary" onClick={irHoy}>Hoy</button>
              <button className="btn btn-sm btn-secondary" onClick={() => cambiarMes(1)}>Siguiente →</button>
            </div>
          </div>

          <div className="calendario-container">
            <div className="calendario">
              <div className="calendario-encabezado">
                {diasSemana.map(dia => (
                  <div key={dia} className="dia-semana-header">{dia}</div>
                ))}
              </div>
              <div className="calendario-cuerpo">
                {renderCalendario()}
              </div>
            </div>

            {/* Panel lateral */}
            {diaSeleccionado && (
              <div className="dia-detalle">
                <div className="dia-detalle-header">
                  <h4>{diaSeleccionado} de {meses[mesActual.getMonth()]}</h4>
                  <button className="btn-cerrar" onClick={() => setDiaSeleccionado(null)}>✕</button>
                </div>
                
                {(() => {
                  const esDiaPasado = fechaSeleccionada &&
                    !isSameDay(new Date(fechaSeleccionada + 'T00:00:00'), new Date()) &&
                    isPast(new Date(fechaSeleccionada + 'T00:00:00'));

                  if (esDiaPasado) return null;

                  return (
                    <>
                      {!puedeCrearCita && fechaSeleccionada && (
                        <div className="alert alert-warning" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fef3c7', color: '#d97706', borderRadius: '8px' }}>
                          ⚠️ No puedes crear citas en este día
                          {isSameDay(new Date(fechaSeleccionada + 'T00:00:00'), new Date()) && horarioDia && !horarioDia.abierto && ' (local cerrado hoy)'}
                          {isSameDay(new Date(fechaSeleccionada + 'T00:00:00'), new Date()) && horarioDia?.cierre && new Date().toTimeString().substring(0,5) >= horarioDia.cierre && ' (horario terminado)'}
                        </div>
                      )}

                      {totalHorasDisponibles > 0 && (
                        <div className="horas-disponibles-total">
                          <div className="total-horas-badge">
                            <span className="total-horas-numero">{totalHorasDisponibles}</span>
                            <span className="total-horas-texto">horas disponibles</span>
                          </div>
                        </div>
                      )}

                      {horarioDia?.abierto && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ flex: 1, fontSize: '0.82rem', padding: '0.5rem 0.5rem' }}
                            onClick={() => generarImagenHorario('descargar')}
                            disabled={generandoImagen || esHoyFueraHorario}
                          >
                            📥 Descargar
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ flex: 1, fontSize: '0.82rem', padding: '0.5rem 0.5rem' }}
                            onClick={() => generarImagenHorario('whatsapp')}
                            disabled={generandoImagen || esHoyFueraHorario}
                          >
                            📤 WhatsApp
                          </button>
                        </div>
                      )}

                      <button
                        className="btn btn-primary btn-block quick-cita-btn"
                        onClick={handleQuickCitaClick}
                        disabled={!puedeCrearCita && !esHoyFueraHorario}
                      >
                        + Crear Cita Rápida
                      </button>
                    </>
                  );
                })()}
                
                {excepcionesDia.filter(e => e.tipo === 'cerrado').length > 0 && (
                  <div className="excepciones-lista">
                    <h5>📋 Excepciones del día</h5>
                    {excepcionesDia.filter(e => e.tipo === 'cerrado').map((exc, idx) => (
                      <div key={exc.id || idx} className="excepcion-detalle cerrado">
                        <div className="excepcion-icono">🚫</div>
                        <div className="excepcion-info">
                          <div className="excepcion-tipo"><strong>Cerrado</strong></div>
                          {exc.motivo && <div className="excepcion-motivo">{exc.motivo}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(() => {
                  const especiales = excepcionesDia.filter(e => e.tipo === 'horario_especial');

                  const renderCita = (cita, idx) => (
                    <div key={idx} className={`cita-detalle ${cita.estado}`}>
                      <div className="cita-detalle-hora">
                        <span className="hora-badge">{cita.hora_inicio?.substring(0,5)}</span>
                      </div>
                      <div className="cita-detalle-info">
                        <div className="cita-cliente-nombre"><strong>{cita.nombres} {cita.apellidos}</strong></div>
                        <div className="cita-servicio">{cita.servicio_nombre}</div>
                        {cita.notas && <div className="cita-notas">📝 {cita.notas}</div>}
                        <div className="cita-acciones">
                          {fechaSeleccionada >= format(new Date(), 'yyyy-MM-dd') && (
                            <button
                              className="btn btn-xs btn-secondary"
                              onClick={() => { setCitaSeleccionada(cita); setNotasDiagnostico(cita.notas || ''); setShowNotasModal(true); }}
                            >
                              📝 Notas
                            </button>
                          )}
                          <div className={`cita-estado-badge ${cita.estado}`}>
                            {cita.estado === 'confirmada' ? '✓ Confirmada' :
                             cita.estado === 'pendiente' ? '⏰ Pendiente' :
                             cita.estado === 'completada' ? '✅ Completada' : cita.estado}
                          </div>
                        </div>
                      </div>
                    </div>
                  );

                  if (especiales.length === 0) {
                    return citasDia.length > 0 ? (
                      <div className="citas-lista">
                        <h5>📅 Citas del día ({citasDia.length})</h5>
                        {citasDia.map(renderCita)}
                      </div>
                    ) : (
                      <div className="sin-citas"><p>No hay citas programadas para este día</p></div>
                    );
                  }

                  const citasPorEspecial = especiales.map(exc => ({
                    exc,
                    citasBloque: citasDia.filter(cita => {
                      if (!cita.hora_inicio || !exc.hora_inicio || !exc.hora_fin) return false;
                      const t = cita.hora_inicio.substring(0, 5);
                      const i = exc.hora_inicio.substring(0, 5);
                      const f = exc.hora_fin.substring(0, 5);
                      return t >= i && t < f;
                    })
                  }));

                  const citasAsignadasIds = new Set(
                    citasPorEspecial.flatMap(({ citasBloque }) => citasBloque.map(c => c.id))
                  );
                  const citasSinBloque = citasDia.filter(c => !citasAsignadasIds.has(c.id));

                  return (
                    <>
                      {citasPorEspecial.map(({ exc, citasBloque }, idx) => (
                        <div key={exc.id || idx} className="horario-especial-bloque">
                          <div className="horario-especial-bloque-header">
                            <div className="horario-especial-bloque-info">
                              <span>⏰</span>
                              <span className="horario-especial-nombre">{exc.motivo || 'Horario Especial'}</span>
                              <span className="horario-especial-rango">{exc.hora_inicio?.substring(0,5)} - {exc.hora_fin?.substring(0,5)}</span>
                            </div>
                            <span className="horario-especial-count">{citasBloque.length} cita(s)</span>
                          </div>
                          {citasBloque.length > 0 ? (
                            <div className="citas-lista">
                              {citasBloque.map(renderCita)}
                            </div>
                          ) : (
                            <div className="sin-citas-bloque">Sin citas en este horario</div>
                          )}
                        </div>
                      ))}

                      {citasSinBloque.length > 0 && (
                        <div className="citas-lista">
                          <h5>📅 Otras citas ({citasSinBloque.length})</h5>
                          {citasSinBloque.map(renderCita)}
                        </div>
                      )}

                      {citasDia.length === 0 && (
                        <div className="sin-citas"><p>No hay citas programadas para este día</p></div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="calendario-leyenda">
            <div className="leyenda-item"><span className="leyenda-color hoy-color"></span><span>Hoy</span></div>
            <div className="leyenda-item"><span className="leyenda-color tiene-citas-color"></span><span>Con Citas</span></div>
            <div className="leyenda-item"><span className="leyenda-color cerrado-color"></span><span>Cerrado (Excepción)</span></div>
            <div className="leyenda-item"><span className="leyenda-color cerrado-base-color"></span><span>Cerrado (Horario)</span></div>
            <div className="leyenda-item"><span className="leyenda-color especial-color"></span><span>Horario Especial</span></div>
          </div>
        </div>

        {/* Stats — primero para vista rápida */}
        <div className="stats-grid">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className={`stat-card stat-${stat.color} ${stat.onClick ? 'clickable' : ''}`}
              onClick={stat.onClick}
              style={{ cursor: stat.onClick ? 'pointer' : 'default' }}
            >
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-content">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-title">{stat.title}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Lista de espera */}
        {listaEspera.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 className="card-title">📋 Lista de espera ({listaEspera.length})</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Contacto</th>
                    <th>Servicio</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {listaEspera.map(item => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: '600' }}>{item.nombre} {item.apellido}</td>
                      <td>
                        <div>📞 {item.telefono}</div>
                        {item.email && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>✉️ {item.email}</div>}
                      </td>
                      <td>{item.servicio_nombre || '—'}</td>
                      <td>{item.fecha}</td>
                      <td>
                        <span className={`badge ${item.estado === 'esperando' ? 'badge-warning' : item.estado === 'notificado' ? 'badge-info' : 'badge-success'}`}>
                          {item.estado}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleEsperaEstado(item.id, 'notificado')}
                          >✉️ Notificado</button>
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleEsperaEstado(item.id, 'convertido')}
                          >✅ Convertido</button>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => handleEsperaEliminar(item.id)}
                          >🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showNuevoClienteModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  👥
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nuevo Cliente
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos para agregar un nuevo cliente
                  </p>
                </div>
              </div>
              <button
                onClick={handleCerrarModalNuevoCliente}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleCrearCliente}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',        label: 'Nombres',     required: true, placeholder: 'Ej. Juan Carlos' },
                      { key: 'apellidos',      label: 'Apellidos',   required: true, placeholder: 'Ej. Pérez López' },
                      { key: 'identificacion', label: 'Cédula / RUC', required: true, placeholder: '1234567890',
                        onChange: (e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 13); setNuevoClienteForm({...nuevoClienteForm, identificacion: v}); } },
                      { key: 'whatsapp_number', label: 'WhatsApp', placeholder: '0999 999 999', type: 'tel',
                        onChange: (e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 15); setNuevoClienteForm({...nuevoClienteForm, whatsapp_number: v}); } },
                      { key: 'email', label: 'Email', type: 'email', placeholder: 'correo@ejemplo.com' },
                    ].map(({ key, label, required, type, placeholder, onChange }) => (
                      <div key={key} className="form-group" style={key === 'email' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input
                          type={type || 'text'}
                          className="form-input"
                          value={nuevoClienteForm[key]}
                          onChange={onChange || ((e) => setNuevoClienteForm({...nuevoClienteForm, [key]: e.target.value}))}
                          required={!!required}
                          placeholder={placeholder}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCerrarModalNuevoCliente}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  + Crear cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNuevoServicioModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  💼
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nuevo Servicio
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos para agregar un nuevo servicio
                  </p>
                </div>
              </div>
              <button
                onClick={handleCerrarModalNuevoServicio}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleCrearServicio}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>💼 Datos del servicio</span>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                      Nombre <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={nuevoServicioForm.nombre}
                      onChange={(e) => setNuevoServicioForm({ ...nuevoServicioForm, nombre: e.target.value })}
                      required
                      placeholder="Ej. Corte de cabello"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                      Descripción
                    </label>
                    <textarea
                      className="form-input"
                      value={nuevoServicioForm.descripcion}
                      onChange={(e) => setNuevoServicioForm({ ...nuevoServicioForm, descripcion: e.target.value })}
                      placeholder="Descripción breve del servicio"
                      rows={2}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Duración (min) <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="number"
                        className="form-input"
                        value={nuevoServicioForm.duracion_minutos}
                        onChange={(e) => setNuevoServicioForm({ ...nuevoServicioForm, duracion_minutos: e.target.value })}
                        required
                        min={1}
                        max={120}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                        Precio ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={nuevoServicioForm.precio}
                        onChange={(e) => setNuevoServicioForm({ ...nuevoServicioForm, precio: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCerrarModalNuevoServicio}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  + Crear servicio
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNuevoProfesionalModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  👨‍💼
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nuevo Profesional
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos básicos del profesional
                  </p>
                </div>
              </div>
              <button
                onClick={handleCerrarModalNuevoProfesional}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleCrearProfesional}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👨‍💼 Datos del profesional</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',     label: 'Nombres',   required: true, placeholder: 'Ej. Ana' },
                      { key: 'apellidos',   label: 'Apellidos', required: true, placeholder: 'Ej. Torres' },
                      { key: 'email',       label: 'Email',     type: 'email', placeholder: 'correo@ejemplo.com' },
                      { key: 'telefono',    label: 'Teléfono',  placeholder: '0999 999 999' },
                      { key: 'especialidad', label: 'Especialidad', placeholder: 'Ej. Estilista', gridColumn: '1 / -1' },
                    ].map(({ key, label, required, type, placeholder, gridColumn }) => (
                      <div key={key} className="form-group" style={gridColumn ? { gridColumn } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input
                          type={type || 'text'}
                          className="form-input"
                          value={nuevoProfesionalForm[key]}
                          onChange={(e) => setNuevoProfesionalForm({ ...nuevoProfesionalForm, [key]: e.target.value })}
                          required={!!required}
                          placeholder={placeholder}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCerrarModalNuevoProfesional}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  + Crear profesional
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modales existentes... */}
      {showCitasPendientesModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📊
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Citas Pendientes ({citasPendientesLista.length})
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Información detallada
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCitasPendientesModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              {citasPendientesLista.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No hay citas pendientes</p>
              ) : (
                <div className="citas-lista-modal">
                  {citasPendientesLista.map(cita => (
                    <div key={cita.id} className="cita-modal-item" style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                      <div className="cita-modal-fecha" style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {normalizarFecha(cita.fecha_cita)} {cita.hora_inicio?.substring(0,5)}
                      </div>
                      <div className="cita-modal-cliente">
                        {cita.nombres} {cita.apellidos}
                      </div>
                      <div className="cita-modal-servicio" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {cita.servicio_nombre}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowCitasPendientesModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showServiciosActivosModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📊
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Servicios Activos ({serviciosActivosLista.length})
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Información detallada
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowServiciosActivosModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              {serviciosActivosLista.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No hay servicios activos</p>
              ) : (
                <div className="servicios-lista-modal">
                  {serviciosActivosLista.map(servicio => (
                    <div key={servicio.id} className="servicio-modal-item" style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                      <div className="servicio-modal-nombre" style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {servicio.nombre}
                      </div>
                      <div className="servicio-modal-detalle" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {servicio.duracion_minutos} min - ${servicio.precio}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowServiciosActivosModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showFueraHorarioModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setShowFueraHorarioModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body" style={{ padding: '2rem 1.5rem' }}>
              <p style={{ fontSize: '1.1rem', marginBottom: 0 }}>No se pueden crear más citas el día de hoy.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowFueraHorarioModal(false)}>Entendido</button>
            </div>
          </div>
        </div>
      )}

      {showQuickCitaModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📊
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Nueva Cita - {quickCitaForm.fecha_cita}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Completa los datos para agregar una nueva cita
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowQuickCitaModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleQuickCitaSubmit}>
              <div className="modal-body">
                {/* Cliente */}
                <div className="form-group">
                  <label className="form-label">Cliente o prospecto *</label>
                  {(() => {
                    // Sin distinguir mayúsculas ni tildes: "raul" encuentra "Raúl"
                    const normalizar = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
                    const termino = normalizar(busquedaContacto.trim());
                    const coincide = (x) => !termino ||
                      normalizar(`${x.nombres || ''} ${x.apellidos || ''} ${x.whatsapp_number || ''} ${x.identificacion || ''}`)
                        .includes(termino);
                    const clientesFiltrados = clientes.filter(coincide);
                    const prospectosFiltrados = prospectosQuick.filter(coincide);
                    const MAX_PROSPECTOS = 100;
                    return (
                      <>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="🔍 Buscar por nombre o número..."
                          value={busquedaContacto}
                          onChange={(e) => setBusquedaContacto(e.target.value)}
                          style={{ marginBottom: '0.5rem' }}
                        />
                        <select
                          className="form-select"
                          value={contactoQuick}
                          onChange={(e) => setContactoQuick(e.target.value)}
                          required
                        >
                          <option value="">
                            Seleccionar... ({clientesFiltrados.length} clientes, {prospectosFiltrados.length} prospectos)
                          </option>
                          {clientesFiltrados.length > 0 && (
                            <optgroup label="Clientes">
                              {clientesFiltrados.map(c => (
                                <option key={`c${c.id}`} value={`c:${c.id}`}>
                                  {c.nombres} {c.apellidos}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {prospectosFiltrados.length > 0 && (
                            <optgroup label={prospectosFiltrados.length > MAX_PROSPECTOS
                              ? `Prospectos (primeros ${MAX_PROSPECTOS}, usa el buscador)`
                              : 'Prospectos'}>
                              {prospectosFiltrados.slice(0, MAX_PROSPECTOS).map(p => (
                                <option key={`p${p.id}`} value={`p:${p.id}`}>
                                  {p.nombres} {p.apellidos}{p.whatsapp_number ? ` · ${p.whatsapp_number}` : ''}{p.etapa ? ` · ${p.etapa}` : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </>
                    );
                  })()}
                </div>

                {/* Servicio */}
                <div className="form-group">
                  <label className="form-label">Servicio *</label>
                  <div className="custom-service-combobox" style={{ position: 'relative' }}>
                    <div
                      className="combobox-header"
                      onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                      style={{
                        border: '2px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        backgroundColor: '#fafafa',
                        width: '100%',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        if (!showServiceDropdown) {
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                          e.currentTarget.style.backgroundColor = '#fafafa';
                        }
                      }}
                    >
                      <span>
                        {quickCitaForm.servicio_id
                          ? (() => {
                              const servicio = servicios.find(s => s.id == quickCitaForm.servicio_id);
                              return servicio ? `${servicio.nombre} - $${servicio.precio} - ${servicio.duracion_minutos}min` : 'Seleccionar servicio...';
                            })()
                          : 'Seleccionar servicio...'
                        }
                      </span>
                      <span className="arrow" style={{ transform: showServiceDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>▼</span>
                    </div>

                    {showServiceDropdown && (
                      <div
                        className="combobox-dropdown"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '2px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '8px',
                          marginTop: '4px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <div
                          className="dropdown-header"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            padding: '10px 12px',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            borderRadius: '6px 6px 0 0'
                          }}
                        >
                          <span>📋 Servicio</span>
                          <span>💰 Precio</span>
                          <span>⏱️ Duración</span>
                        </div>

                        {servicios.map(servicio => (
                          <div
                            key={servicio.id}
                            className={`service-option ${quickCitaForm.servicio_id == servicio.id ? 'selected' : ''}`}
                            onClick={() => {
                              setQuickCitaForm({...quickCitaForm, servicio_id: servicio.id});
                              cargarHorasDisponiblesParaCita(fechaSeleccionada, servicio.id);
                              setShowServiceDropdown(false);
                            }}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '2fr 1fr 1fr',
                              padding: '12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              backgroundColor: quickCitaForm.servicio_id == servicio.id ? '#e8eaff' : 'white',
                              transition: 'all 0.2s',
                              fontSize: '0.95rem'
                            }}
                            onMouseEnter={(e) => {
                              if (quickCitaForm.servicio_id != servicio.id) {
                                e.currentTarget.style.backgroundColor = '#f5f7ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (quickCitaForm.servicio_id != servicio.id) {
                                e.currentTarget.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            <span style={{ color: '#333', fontWeight: quickCitaForm.servicio_id == servicio.id ? '600' : '400' }}>
                              {servicio.nombre}
                            </span>
                            <span style={{ color: '#667eea', fontWeight: quickCitaForm.servicio_id == servicio.id ? '600' : '500' }}>
                              ${servicio.precio}
                            </span>
                            <span style={{ color: '#764ba2', fontWeight: quickCitaForm.servicio_id == servicio.id ? '600' : '500' }}>
                              {servicio.duracion_minutos} min
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Fecha (solo lectura) */}
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={quickCitaForm.fecha_cita}
                    readOnly
                    disabled
                  />
                </div>

                {/* Hora */}
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Hora *</label>
                  <input
                    type="time"
                    className="form-input"
                    value={quickCitaForm.hora_inicio}
                    onChange={(e) => setQuickCitaForm({...quickCitaForm, hora_inicio: e.target.value})}
                    required
                    style={{ border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa', width: '100%', transition: 'all 0.2s' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#ffffff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                  />
                  {horasDisponiblesCita.length === 0 && quickCitaForm.servicio_id && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '0.5rem 0.75rem' }}>
                      <span>🚫</span><span>El negocio está cerrado o sin horario disponible en esta fecha.</span>
                    </div>
                  )}
                  {horasDisponiblesCita.length > 0 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>✅</span><span>Horas disponibles: {horasDisponiblesCita[0]} - {horasDisponiblesCita[horasDisponiblesCita.length - 1]}</span>
                    </div>
                  )}
                </div>

                {/* Notas */}
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Notas</label>
                  <textarea
                    className="form-textarea"
                    value={quickCitaForm.notas}
                    onChange={(e) => setQuickCitaForm({...quickCitaForm, notas: e.target.value})}
                    rows="3"
                    style={{ border: '2px solid rgba(59,130,246,0.3)', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', backgroundColor: '#fafafa', width: '100%', transition: 'all 0.2s', resize: 'vertical' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#ffffff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                {/* Recurrencia */}
                <div className="form-group" style={{ marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid #f3f4f6' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontWeight: '600', color: '#374151', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={esRecurrenteDash}
                      onChange={e => setEsRecurrenteDash(e.target.checked)}
                      style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                    />
                    🔁 Cita recurrente
                  </label>
                  {esRecurrenteDash && (
                    <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                      <div className="grid-responsive" style={{ gap: '0.75rem' }}>
                        <div>
                          <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.3rem' }}>Frecuencia</label>
                          <select value={recurrenciaDash.tipo} onChange={e => setRecurrenciaDash(r => ({ ...r, tipo: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.875rem', background: '#fff' }}>
                            <option value="semanal">Cada semana</option>
                            <option value="quincenal">Cada 2 semanas</option>
                            <option value="mensual">Cada mes</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.3rem' }}>Terminar</label>
                          <select value={recurrenciaDash.fin_tipo} onChange={e => setRecurrenciaDash(r => ({ ...r, fin_tipo: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.875rem', background: '#fff' }}>
                            <option value="sesiones">Por número de sesiones</option>
                            <option value="fecha">Hasta una fecha</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ marginTop: '0.75rem' }}>
                        {recurrenciaDash.fin_tipo === 'sesiones' ? (
                          <div>
                            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.3rem' }}>Número de sesiones (máx. 52)</label>
                            <input type="number" min="2" max="52" value={recurrenciaDash.num_sesiones} onChange={e => setRecurrenciaDash(r => ({ ...r, num_sesiones: parseInt(e.target.value) || 2 }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.875rem', background: '#fff' }} />
                          </div>
                        ) : (
                          <div>
                            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.3rem' }}>Fecha límite</label>
                            <input type="date" value={recurrenciaDash.fecha_fin} min={quickCitaForm.fecha_cita} onChange={e => setRecurrenciaDash(r => ({ ...r, fecha_fin: e.target.value }))} style={{ width: '100%', padding: '0.5rem', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '0.875rem', background: '#fff' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuickCitaModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {esRecurrenteDash ? 'Crear Serie Recurrente' : 'Crear Cita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showNotasModal && citaSeleccionada && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  📊
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    Notas - {citaSeleccionada.nombres} {citaSeleccionada.apellidos}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    Información detallada
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowNotasModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <textarea 
                value={notasDiagnostico}
                onChange={(e) => setNotasDiagnostico(e.target.value)}
                rows="6"
                placeholder="Ingresa las notas o diagnóstico..."
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNotasModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleGuardarNotas}>Guardar Notas</button>
            </div>
          </div>
        </div>
      )}

      {/* Plantilla oculta para generar imagen */}
      {fechaSeleccionada && horarioDia?.abierto && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
          <div ref={imagenRef} style={{
            width: '400px',
            background: '#ffffff',
            borderRadius: '20px',
            padding: '2.5rem 2rem',
            fontFamily: "'Segoe UI', Arial, sans-serif",
            color: '#111827',
            boxSizing: 'border-box',
            border: '1px solid #e5e7eb'
          }}>
            {/* Logo FactuTracks */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>🎯</span>
              <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#3b82f6' }}>FactuTracks</span>
            </div>

            {/* Separador */}
            <div style={{ height: '2px', background: '#e5e7eb', marginBottom: '1.5rem' }} />

            {/* Nombre empresa */}
            <div style={{ fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.25rem' }}>
              Negocio
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '1.5rem', lineHeight: 1.2, color: '#111827' }}>
              {user?.tenant?.nombre || 'Mi Negocio'}
            </div>

            {/* Fecha */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.3rem' }}>Fecha</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>
                {diasNombres[new Date(fechaSeleccionada + 'T00:00:00').getDay()]}{', '}
                {new Date(fechaSeleccionada + 'T00:00:00').getDate()} de {meses[new Date(fechaSeleccionada + 'T00:00:00').getMonth()]} {new Date(fechaSeleccionada + 'T00:00:00').getFullYear()}
              </div>
            </div>

            {/* Horario */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.3rem' }}>Horario de atención</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#3b82f6' }}>
                {horarioDia.apertura} — {horarioDia.cierre}
              </div>
            </div>

            {/* Horas disponibles */}
            {totalHorasDisponibles > 0 && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#3b82f6' }}>{totalHorasDisponibles}</div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', lineHeight: 1.3 }}>horas<br/>disponibles</div>
              </div>
            )}

            {/* Footer */}
            <div style={{ height: '1px', background: '#f3f4f6', marginBottom: '1rem' }} />
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center', letterSpacing: '1px' }}>
              Reserva tu cita en línea • factutracks.com
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
